import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ExpertRecord } from "./circle-experts-logic";

export type CircleExpertRow = ExpertRecord & {
  origin: "appointed" | "applied";
  note: string | null;
  created_at: string;
};

const COLS = "circle_id, user_id, title, status, origin, note, created_at";

/** Все видимые мне записи сообщества (активные + свои/хранительские заявки — по RLS). */
export function useCircleExperts(circleId: string | undefined) {
  return useQuery({
    queryKey: ["circle-experts", circleId],
    enabled: !!circleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_experts")
        .select(COLS)
        .eq("circle_id", circleId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleExpertRow[];
    },
  });
}

/** Есть ли активные эксперты в любом из сообществ. */
export async function hasActiveExpertsIn(circleIds: string[]): Promise<boolean> {
  if (circleIds.length === 0) return false;
  const { count } = await supabase
    .from("circle_experts")
    .select("user_id", { count: "exact", head: true })
    .in("circle_id", circleIds)
    .eq("status", "active");
  return (count ?? 0) > 0;
}

export type ExpertBadge = { circle_id: string; circle_name: string; title: string | null };

/** Активные статусы человека в опубликованных сообществах. */
export function useUserExpertBadges(userId: string | undefined) {
  return useQuery({
    queryKey: ["user-expert-badges", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_experts")
        .select("circle_id, title, created_at, circles!inner(name, status, is_deleted)")
        .eq("user_id", userId!)
        .eq("status", "active")
        .eq("circles.status", "published")
        .eq("circles.is_deleted", false)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        circle_id: r.circle_id as string,
        circle_name: r.circles?.name as string,
        title: (r.title as string | null) ?? null,
      })) as ExpertBadge[];
    },
  });
}

/** Опубликованные сообщества, где я хранитель или активный эксперт (для формы события). */
export async function fetchMyEventCircles(uid: string): Promise<{ id: string; name: string }[]> {
  const pub = (q: any) => q.eq("status", "published").eq("is_deleted", false);
  const [own, co, ex] = await Promise.all([
    pub(supabase.from("circles").select("id, name").eq("host_user_id", uid)),
    supabase.from("circle_hosts").select("circles!inner(id, name, status, is_deleted)").eq("user_id", uid),
    supabase
      .from("circle_experts")
      .select("circles!inner(id, name, status, is_deleted)")
      .eq("user_id", uid)
      .eq("status", "active"),
  ]);
  const map = new Map<string, string>();
  for (const c of (own.data ?? []) as { id: string; name: string }[]) map.set(c.id, c.name);
  for (const r of [...((co.data ?? []) as any[]), ...((ex.data ?? []) as any[])]) {
    const c = r.circles;
    if (c && c.status === "published" && !c.is_deleted) map.set(c.id, c.name);
  }
  return [...map].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "ru"));
}

/** Названия сообществ по id (для плашки сообщества). */
export function useCircleNames(ids: (string | null | undefined)[]) {
  const list = [...new Set(ids.filter((x): x is string => !!x))].sort();
  return useQuery({
    queryKey: ["circle-names", list.join(",")],
    enabled: list.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase.from("circles").select("id, name").in("id", list).eq("is_deleted", false);
      return new Map((data ?? []).map((c) => [c.id as string, c.name as string]));
    },
  });
}
